import { useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Skeleton, Stack, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { FoodsIcon, FoodsTags } from "@foodpilot/foods";
import { z } from "zod";
import { useGetConsentPreferences } from "src/api/client-api/consent/useGetConsentPreferences";
import { useUpdateConsentPreferences } from "src/api/client-api/consent/useUpdateConsentPreferences";
import { useGetContractualAcceptances } from "src/api/client-api/consent/useGetContractualAcceptances";
import { useSnackbar } from "src/utils/useSnackbar";
import { ConsentPreferenceRow } from "src/components/Consent/ConsentPreferenceRow";
import { ContractualAcceptanceRow } from "src/components/Consent/ContractualAcceptanceRow";
import {
    ConsentDocumentPreviewDialog,
    type ConsentDocumentPreview,
} from "src/components/Consent/ConsentDocumentPreviewDialog";
import {
    DOCUMENT_CATEGORY_KEYS,
    getConsentCategoryConfig,
    getConsentCategoryKey,
    OPT_IN_CATEGORY_KEYS,
} from "src/components/Consent/consentCategoryConfig";
import type { ConsentPreference } from "src/api/client-api/consent/useGetConsentPreferences";
import type { ContractualAcceptance } from "src/api/client-api/consent/useGetContractualAcceptances";

const ConsentPreferencesSchema = z.object({
    selectedItemIds: z.array(z.number()),
});

type ConsentPreferencesFormValues = z.infer<typeof ConsentPreferencesSchema>;

const sectionStyles = {
    backgroundColor: "white",
    border: "1px solid #E0DEDD",
};

type ConsentPreferencesPanelBaseProps = {
    acceptAllFallback?: string;
    acceptAllKey?: string;
    descriptionFallback: string;
    descriptionKey: string;
    rejectAllFallback?: string;
    rejectAllKey?: string;
    showBulkActions?: boolean;
    showFooterSave?: boolean;
    showHeaderSave?: boolean;
    titleFallback: string;
    titleKey: string;
};

type ContractualAcceptancesPanelBaseProps = {
    descriptionFallback: string;
    descriptionKey: string;
    readOnlyFallback: string;
    readOnlyKey: string;
    titleFallback: string;
    titleKey: string;
};

const getCategoryGroupKey = (category?: string | null) =>
    getConsentCategoryKey(category) ?? category?.trim() ?? "other";

const inferDocumentCategoryGroupKey = (acceptance: ContractualAcceptance) => {
    const categoryKey = getConsentCategoryKey(acceptance.category);

    if (categoryKey) {
        return categoryKey;
    }

    const searchableDocumentText = [acceptance.code, acceptance.name, acceptance.title].join(" ").toLowerCase();

    if (/(^|[\s_-])(cgu|terms?|conditions?)([\s_-]|$)/.test(searchableDocumentText)) {
        return "cgu";
    }

    if (/platform|plateforme|policy|politique/.test(searchableDocumentText)) {
        return "platform_policy";
    }

    return acceptance.category?.trim() ?? "other";
};

const ConsentCategoryHeader = ({
    categoryKey,
    count,
    isFirst,
}: {
    categoryKey: string;
    count: number;
    isFirst: boolean;
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const knownCategoryKey = getConsentCategoryKey(categoryKey);
    const categoryConfig = getConsentCategoryConfig(categoryKey);
    const categoryLabel =
        knownCategoryKey ? t(categoryConfig.labelKey, categoryConfig.labelFallback)
        : categoryKey === "other" ? t("settings.consent.category.unknown", "Uncategorized")
        : categoryKey;
    const categoryColor =
        categoryConfig.colorKey === "grey" ? theme.custom.grey[2000] : theme.palette[categoryConfig.colorKey].main;
    const categoryTagSx = {
        height: 22,
        fontSize: "11px",
        fontWeight: 700,
        bgcolor: alpha(categoryColor, 0.08),
        color: categoryColor,
        borderRadius: "6px",
    };

    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
                px: theme.spacing(4),
                py: theme.spacing(1.5),
                bgcolor: theme.palette.grey[50],
                borderBottom: `1px solid ${theme.custom.grey[500]}`,
                borderTop: isFirst ? "none" : `1px solid ${theme.custom.grey[500]}`,
            }}
        >
            <Box
                sx={{
                    display: "grid",
                    placeItems: "center",
                    width: 32,
                    height: 32,
                    flexShrink: 0,
                    borderRadius: theme.spacing(1),
                    bgcolor: alpha(categoryColor, 0.08),
                    color: categoryColor,
                }}
            >
                <FoodsIcon icon={categoryConfig.icon} size={2} />
            </Box>
            <Chip label={`${categoryLabel} (${count})`} size="small" sx={categoryTagSx} />
        </Stack>
    );
};

const ConsentPreferencesPanelBase = ({
    acceptAllFallback = "Accept all",
    acceptAllKey = "profileSettings.preferences.consent.accept_all",
    descriptionFallback,
    descriptionKey,
    rejectAllFallback = "Reject all",
    rejectAllKey = "profileSettings.preferences.consent.reject_all",
    showBulkActions = false,
    showFooterSave = true,
    showHeaderSave = false,
    titleFallback,
    titleKey,
}: ConsentPreferencesPanelBaseProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const {
        consentPreferences,
        isLoading: isConsentPreferencesLoading,
        isFetching: isConsentPreferencesFetching,
    } = useGetConsentPreferences();
    const { mutate: updateConsentPreferences, isLoading: isSavingPreferences } = useUpdateConsentPreferences();
    const methods = useForm<ConsentPreferencesFormValues>({
        defaultValues: {
            selectedItemIds: [],
        },
        resolver: zodResolver(ConsentPreferencesSchema),
    });

    const { formState, handleSubmit, reset, setValue, watch } = methods;
    const [previewDocument, setPreviewDocument] = useState<ConsentDocumentPreview | null>(null);
    const consentPreferenceItems = consentPreferences ?? [];
    const selectedPreferenceIds = useMemo(
        () => consentPreferenceItems.filter((preference) => preference.enabled).map((preference) => preference.itemId),
        [consentPreferenceItems],
    );

    const watchedSelectedIds = watch("selectedItemIds");

    useEffect(() => {
        reset({
            selectedItemIds: selectedPreferenceIds,
        });
    }, [reset, selectedPreferenceIds]);

    const saveConsentPreferences = handleSubmit((values) => {
        if (consentPreferenceItems.length === 0) {
            return;
        }

        updateConsentPreferences(
            {
                preferences: consentPreferenceItems.map((preference) => ({
                    itemId: preference.itemId,
                    enabled: values.selectedItemIds.includes(preference.itemId),
                })),
            },
            {
                onSuccess: () => {
                    reset(values);
                    setSnackbarProps({
                        message: t("companySettings.consent.preferences.saved", "Consent preferences saved."),
                        type: "success",
                    });
                },
                onError: (error) => {
                    setSnackbarProps({
                        message: error instanceof Error ? error.message : t("une_erreur_s_est_produite"),
                        type: "error",
                        forceAutoHideInMilisecond: 5000,
                    });
                },
            },
        );
    });

    const handleAcceptAll = () => {
        setValue(
            "selectedItemIds",
            consentPreferenceItems.map((p) => p.itemId),
            { shouldDirty: true },
        );
    };

    const handleRejectAll = () => {
        setValue("selectedItemIds", [], { shouldDirty: true });
    };

    const togglePreference = (itemId: number, enabled: boolean) => {
        const current = methods.getValues("selectedItemIds");
        const next = enabled ? [...current, itemId] : current.filter((id) => id !== itemId);
        setValue("selectedItemIds", next, { shouldDirty: true });
    };

    // Group preferences by category
    const groupedPreferences = useMemo(() => {
        const groups: Record<string, typeof consentPreferenceItems> = {};
        for (const pref of consentPreferenceItems) {
            const cat = getCategoryGroupKey(pref.category);
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(pref);
        }
        return groups;
    }, [consentPreferenceItems]);

    const sortedCategories = useMemo(() => {
        const ordered = OPT_IN_CATEGORY_KEYS.filter((cat) => groupedPreferences[cat]);
        const remaining = Object.keys(groupedPreferences).filter(
            (cat) => !OPT_IN_CATEGORY_KEYS.includes(cat as (typeof OPT_IN_CATEGORY_KEYS)[number]),
        );
        return [...ordered, ...remaining];
    }, [groupedPreferences]);

    return (
        <Box sx={{ ...sectionStyles, borderRadius: theme.spacing(2), overflow: "hidden" }}>
            {/* Header */}
            <Stack
                justifyContent="space-between"
                sx={{
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    px: theme.spacing(4),
                    py: theme.spacing(3),
                    flexDirection: { xs: "column", md: "row" },
                    gap: { xs: theme.spacing(2), md: 0 },
                    alignItems: { xs: "flex-start", md: "center" },
                }}
            >
                <Stack gap={theme.spacing(0.5)}>
                    <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                        {t(titleKey, titleFallback)}
                    </Typography>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                        {t(descriptionKey, descriptionFallback)}
                    </Typography>
                </Stack>
                {showBulkActions && (
                    <Stack direction={{ xs: "column", sm: "row" }} gap={theme.spacing(1)}>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={handleRejectAll}
                            disabled={
                                consentPreferenceItems.length === 0 ||
                                isSavingPreferences ||
                                isConsentPreferencesLoading
                            }
                        >
                            {t(rejectAllKey, rejectAllFallback)}
                        </Button>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={handleAcceptAll}
                            disabled={
                                consentPreferenceItems.length === 0 ||
                                isSavingPreferences ||
                                isConsentPreferencesLoading
                            }
                        >
                            {t(acceptAllKey, acceptAllFallback)}
                        </Button>
                    </Stack>
                )}
                {showHeaderSave && (
                    <Button
                        variant="primary"
                        size="small"
                        onClick={saveConsentPreferences}
                        disabled={
                            consentPreferenceItems.length === 0 ||
                            isSavingPreferences ||
                            isConsentPreferencesLoading ||
                            !formState.isDirty
                        }
                    >
                        {isSavingPreferences ? t("common.saving", "Saving...") : t("common.save", "Save")}
                    </Button>
                )}
            </Stack>

            {/* Content */}
            <Stack sx={{ position: "relative" }}>
                {(isConsentPreferencesFetching || isSavingPreferences) && consentPreferenceItems.length > 0 && (
                    <Stack
                        sx={{
                            background: "rgba(255, 255, 255, 0.5)",
                            position: "absolute",
                            inset: theme.spacing(2),
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1,
                            borderRadius: theme.spacing(1),
                        }}
                    >
                        <CircularProgress size={28} />
                    </Stack>
                )}

                {isConsentPreferencesLoading ?
                    <Box>
                        {[0, 1].map((index) => (
                            <Stack
                                key={index}
                                sx={{
                                    minHeight: "64px",
                                    borderBottom: index === 1 ? "none" : `1px solid ${theme.custom.grey[500]}`,
                                    px: theme.spacing(4),
                                    py: theme.spacing(3),
                                    gap: theme.spacing(2),
                                }}
                            >
                                <Skeleton variant="text" width={220} height={28} />
                                <Skeleton variant="rounded" height={36} width="100%" />
                            </Stack>
                        ))}
                    </Box>
                : consentPreferenceItems.length === 0 ?
                    <Stack gap={theme.spacing(1)} sx={{ px: theme.spacing(4), py: theme.spacing(4) }}>
                        <Typography variant="h5">
                            {t("companySettings.consent.preferences.empty.title", "No optional preferences available")}
                        </Typography>
                        <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "companySettings.consent.preferences.empty.description",
                                "There are currently no optional consent items available for this organization.",
                            )}
                        </Typography>
                    </Stack>
                :   <Box>
                        {sortedCategories.map((catKey, catIdx) => {
                            const preferences = groupedPreferences[catKey] ?? [];

                            return (
                                <Box key={catKey}>
                                    <ConsentCategoryHeader
                                        categoryKey={catKey}
                                        count={preferences.length}
                                        isFirst={catIdx === 0}
                                    />

                                    {/* Preference rows */}
                                    {preferences.map((preference, i) => {
                                        const isLastOverall =
                                            i === preferences.length - 1 && catIdx === sortedCategories.length - 1;
                                        const isEnabled = watchedSelectedIds.includes(preference.itemId);
                                        const originalEnabled = selectedPreferenceIds.includes(preference.itemId);
                                        const isModified = isEnabled !== originalEnabled;

                                        return (
                                            <ConsentPreferenceRow
                                                key={preference.itemId}
                                                preference={preference}
                                                isEnabled={isEnabled}
                                                isModified={isModified}
                                                isLast={isLastOverall}
                                                onPreviewDocument={() =>
                                                    setPreviewDocument(toPreferencePreview(preference))
                                                }
                                                onToggle={(enabled) => togglePreference(preference.itemId, enabled)}
                                            />
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                }
            </Stack>

            {/* Footer with save */}
            {consentPreferenceItems.length > 0 && (
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: theme.spacing(4),
                        py: theme.spacing(2),
                        borderTop: `1px solid ${theme.custom.grey[500]}`,
                        bgcolor: formState.isDirty ? theme.palette.primary[50] : "transparent",
                        transition: "background-color 0.2s",
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: formState.isDirty ? theme.palette.primary.main : theme.custom.grey[2000],
                            fontWeight: formState.isDirty ? 500 : 400,
                        }}
                    >
                        {formState.isDirty ?
                            t("consent.preferences.unsaved", "Unsaved changes")
                        :   t("consent.preferences.no_changes", "No changes pending")}
                    </Typography>
                    {showFooterSave && (
                        <Button
                            variant="primary"
                            size="small"
                            onClick={saveConsentPreferences}
                            disabled={
                                consentPreferenceItems.length === 0 ||
                                isSavingPreferences ||
                                isConsentPreferencesLoading ||
                                !formState.isDirty
                            }
                        >
                            {isSavingPreferences ? t("common.saving", "Saving...") : t("common.save", "Save")}
                        </Button>
                    )}
                </Stack>
            )}

            {previewDocument && (
                <ConsentDocumentPreviewDialog
                    open={!!previewDocument}
                    onClose={() => setPreviewDocument(null)}
                    {...previewDocument}
                />
            )}
        </Box>
    );
};

const ContractualAcceptancesPanelBase = ({
    descriptionFallback,
    descriptionKey,
    readOnlyFallback,
    readOnlyKey,
    titleFallback,
    titleKey,
}: ContractualAcceptancesPanelBaseProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { contractualAcceptances, isLoading: isContractualAcceptancesLoading } = useGetContractualAcceptances();

    const [previewDocument, setPreviewDocument] = useState<ConsentDocumentPreview | null>(null);
    const groupedContractualAcceptances = useMemo(() => {
        const groups: Record<string, ContractualAcceptance[]> = {};

        for (const acceptance of contractualAcceptances ?? []) {
            const categoryKey = inferDocumentCategoryGroupKey(acceptance);

            if (!groups[categoryKey]) {
                groups[categoryKey] = [];
            }

            groups[categoryKey].push(acceptance);
        }

        return groups;
    }, [contractualAcceptances]);
    const sortedAcceptanceCategories = useMemo(() => {
        const ordered = DOCUMENT_CATEGORY_KEYS.filter((categoryKey) => groupedContractualAcceptances[categoryKey]);
        const remaining = Object.keys(groupedContractualAcceptances).filter(
            (categoryKey) => !DOCUMENT_CATEGORY_KEYS.includes(categoryKey as (typeof DOCUMENT_CATEGORY_KEYS)[number]),
        );

        return [...ordered, ...remaining];
    }, [groupedContractualAcceptances]);

    return (
        <Box sx={{ ...sectionStyles, borderRadius: theme.spacing(2), overflow: "hidden" }}>
            <Stack
                sx={{
                    borderBottom: `1px solid ${theme.custom.grey[500]}`,
                    px: { xs: theme.spacing(2), md: theme.spacing(4) },
                    py: theme.spacing(3),
                    gap: theme.spacing(0.5),
                }}
            >
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    gap={theme.spacing(1.5)}
                >
                    <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
                        {t(titleKey, titleFallback)}
                    </Typography>
                    <FoodsTags label={t(readOnlyKey, readOnlyFallback)} variant="tags-red" />
                </Stack>
                <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                    {t(descriptionKey, descriptionFallback)}
                </Typography>
            </Stack>

            {isContractualAcceptancesLoading ?
                <Box>
                    {[0, 1].map((index) => (
                        <Stack
                            key={index}
                            sx={{
                                minHeight: "64px",
                                borderBottom: index === 1 ? "none" : `1px solid ${theme.custom.grey[500]}`,
                                px: theme.spacing(4),
                                py: theme.spacing(3),
                                gap: theme.spacing(1.5),
                            }}
                        >
                            <Skeleton variant="text" width={260} height={28} />
                            <Skeleton variant="text" width={180} height={22} />
                        </Stack>
                    ))}
                </Box>
            : contractualAcceptances === undefined || contractualAcceptances.length === 0 ?
                <Stack gap={theme.spacing(1)} sx={{ px: theme.spacing(4), py: theme.spacing(4) }}>
                    <Typography variant="h5">
                        {t("companySettings.consent.history.empty.title", "No acceptance recorded yet")}
                    </Typography>
                    <Typography variant="body" sx={{ color: theme.custom.grey[2000] }}>
                        {t(
                            "companySettings.consent.history.empty.description",
                            "Accepted mandatory documents will appear here once they are validated.",
                        )}
                    </Typography>
                </Stack>
            :   <Box>
                    {sortedAcceptanceCategories.map((categoryKey, categoryIndex) => {
                        const acceptances = groupedContractualAcceptances[categoryKey] ?? [];

                        return (
                            <Box key={categoryKey}>
                                <ConsentCategoryHeader
                                    categoryKey={categoryKey}
                                    count={acceptances.length}
                                    isFirst={categoryIndex === 0}
                                />
                                {acceptances.map((acceptance, acceptanceIndex) => {
                                    const isLastOverall =
                                        acceptanceIndex === acceptances.length - 1 &&
                                        categoryIndex === sortedAcceptanceCategories.length - 1;

                                    return (
                                        <ContractualAcceptanceRow
                                            key={acceptance.recordId}
                                            acceptance={acceptance}
                                            isLast={isLastOverall}
                                            onViewDocument={() => setPreviewDocument(toAcceptancePreview(acceptance))}
                                            showCategoryBadge={false}
                                        />
                                    );
                                })}
                            </Box>
                        );
                    })}
                </Box>
            }

            {previewDocument && (
                <ConsentDocumentPreviewDialog
                    open={!!previewDocument}
                    onClose={() => setPreviewDocument(null)}
                    {...previewDocument}
                />
            )}
        </Box>
    );
};

export const ProfileConsentPreferencesPanel = () => (
    <ConsentPreferencesPanelBase
        titleKey="profileSettings.preferences.consent.title"
        titleFallback="Communication preferences"
        descriptionKey="profileSettings.preferences.consent.description"
        descriptionFallback="You can change these at any time without affecting your access."
        showBulkActions
        showFooterSave
    />
);

export const ProfileContractualAcceptancesPanel = () => (
    <ContractualAcceptancesPanelBase
        titleKey="profileSettings.acceptances.title"
        titleFallback="Contractual acceptances"
        descriptionKey="profileSettings.acceptances.description"
        descriptionFallback="Review the required documents you have already accepted for the current organization."
        readOnlyKey="consent.acceptances.read_only"
        readOnlyFallback="Read-only"
    />
);

export const CompanyConsentSettingsPanel = () => {
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(3)}>
            <ConsentPreferencesPanelBase
                titleKey="companySettings.consent.preferences.title"
                titleFallback="Consent & preferences"
                descriptionKey="companySettings.consent.preferences.description"
                descriptionFallback="Manage your optional communication and feature opt-ins for the current organization."
                showHeaderSave
                showFooterSave={false}
            />
            <ContractualAcceptancesPanelBase
                titleKey="companySettings.consent.history.title"
                titleFallback="Contractual acceptance history"
                descriptionKey="companySettings.consent.history.description"
                descriptionFallback="Review the mandatory documents you already accepted for the current organization."
                readOnlyKey="consent.acceptances.read_only"
                readOnlyFallback="Read-only"
            />
        </Stack>
    );
};

const toPreferencePreview = (preference: ConsentPreference): ConsentDocumentPreview => ({
    category: preference.category,
    content: preference.content,
    description: preference.description,
    documentUrl: preference.documentUrl,
    itemLabel: preference.name,
    summary: preference.summary,
    title: preference.title,
    version: preference.version,
});

const toAcceptancePreview = (acceptance: ContractualAcceptance): ConsentDocumentPreview => ({
    acceptedAt: acceptance.acceptedAt,
    category: acceptance.category,
    content: acceptance.content,
    documentUrl: acceptance.documentUrl,
    itemLabel: acceptance.name,
    summary: acceptance.summary,
    title: acceptance.title,
    version: acceptance.version,
});
